/**
 * Client IFU rule of the DGI SFE annex on client types: the client IFU
 * follows the official format on local transactions and may be free-form on
 * exports. A client type flagged `requiresIFU` (PM, PC) needs one either way.
 */
import { IFU_PATTERN_BF } from "./client-types.js";
import { EXPORT_TYPE_CODES } from "./invoice-types.js";
/** True for the export invoice types EV, ET, EA. */
export function isExportInvoiceType(invoiceType) {
    return EXPORT_TYPE_CODES.includes(invoiceType);
}
/** Trimmed and upper-cased: the form the official pattern is checked on. */
export function normalizeIfu(ifu) {
    return ifu.trim().toUpperCase();
}
/** True when the IFU matches the official format once normalized. */
export function isOfficialIfuFormat(ifu) {
    return !!ifu && IFU_PATTERN_BF.test(normalizeIfu(ifu));
}
function blankToNull(ifu) {
    const trimmed = ifu?.trim();
    return trimmed ? trimmed : null;
}
/**
 * The rule for the IFU an invoice carries. Local types (FV, FT, FA) need the
 * official format, stored normalized; export types (EV, ET, EA) take any
 * non-empty value, stored trimmed. A type that does not require an IFU
 * passes whatever it holds, trimmed.
 */
export function checkInvoiceClientIfu(invoiceType, requirements, ifu) {
    const value = blankToNull(ifu);
    if (!requirements?.requiresIFU)
        return { ok: true, ifu: value };
    if (value === null)
        return { ok: false, issue: "IFU_REQUIRED" };
    if (isExportInvoiceType(invoiceType))
        return { ok: true, ifu: value };
    return isOfficialIfuFormat(value)
        ? { ok: true, ifu: normalizeIfu(value) }
        : { ok: false, issue: "IFU_INVALID" };
}
/**
 * The rule for the IFU stored on a client record, which has no invoice type.
 * A type that requires an IFU needs one. A type that also accepts a NIF (PM)
 * may hold a foreign identifier in free format, for export invoices; any
 * other IFU-bearing type (PC) needs the official format. A value in the
 * official format is stored normalized, any other trimmed.
 */
export function checkClientRecordIfu(requirements, ifu) {
    const value = blankToNull(ifu);
    const stored = value !== null && isOfficialIfuFormat(value) ? normalizeIfu(value) : value;
    if (!requirements?.requiresIFU)
        return { ok: true, ifu: stored };
    if (value === null)
        return { ok: false, issue: "IFU_REQUIRED" };
    if (!requirements.acceptsNIF && !isOfficialIfuFormat(value)) {
        return { ok: false, issue: "IFU_INVALID" };
    }
    return { ok: true, ifu: stored };
}
/**
 * True when a client of these requirements could not be invoiced locally
 * with the IFU it holds: its type requires an IFU and the value is missing
 * or not in the official format.
 */
export function isIfuNonCompliantForLocalInvoice(requirements, ifu) {
    return !!requirements?.requiresIFU && !isOfficialIfuFormat(ifu);
}
//# sourceMappingURL=ifu-rule.js.map